// Alerts Hub server state: the project's settings (with recipients), the
// restricted-area options, and their writes. Setting writes are optimistic
// (`useOptimisticSettingUpdate`); restricted-area writes toast and refetch.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AlertSettingKey, IAlertsHubUpdate, IRestrictedAreaInput } from '@d-orca/shared/alert-kinds';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';
import {
  createRestrictedArea,
  deleteRestrictedArea,
  fetchAlertsHub,
  fetchRestrictedAreaOptions,
  updateAlertsHub,
  updateRestrictedArea,
} from '@apis/alerts';
import { useToast } from '@components/ui/Toasts/toastContext';
import { useOptimisticSettingUpdate } from '@components/AlertSettings/useOptimisticSettingUpdate';

export const alertsHubKey = (siteId: string) => ['alertSettings', 'hub', siteId] as const;
const areaOptionsKey = (siteId: string) => ['alertSettings', 'restrictedAreaOptions', siteId] as const;

export function useAlertsHub(siteId: string) {
  return useQuery({
    queryKey: alertsHubKey(siteId),
    queryFn: () => fetchAlertsHub(siteId),
    refetchOnWindowFocus: false,
  });
}

export function useUpdateAlertsHub(siteId: string) {
  return useOptimisticSettingUpdate(alertsHubKey(siteId), (key: AlertSettingKey, update: IAlertsHubUpdate) =>
    updateAlertsHub(siteId, key, update),
  );
}

/** What a restricted area can be made of — read only when the Hub shows the safety group. */
export function useRestrictedAreaOptions(siteId: string, enabled: boolean) {
  return useQuery({
    queryKey: areaOptionsKey(siteId),
    queryFn: () => fetchRestrictedAreaOptions(siteId),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** One restricted-area write: a toast either way, and the Hub refetched on success. */
function useRestrictedAreaWrite<V>(siteId: string, write: (vars: V) => Promise<unknown>, doneKey: string) {
  const { t } = useTranslation();
  const push = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: write,
    onSuccess: async () => {
      push({ kind: 'success', title: t(doneKey) });
      await queryClient.invalidateQueries({ queryKey: alertsHubKey(siteId) });
    },
    onError: () => push({ kind: 'error', title: t('alertSettings.restrictedArea.toasts.failed') }),
  });
}

export function useCreateRestrictedArea(siteId: string) {
  return useRestrictedAreaWrite(
    siteId,
    (input: IRestrictedAreaInput) => createRestrictedArea(siteId, input),
    'alertSettings.restrictedArea.toasts.created',
  );
}

export function useUpdateRestrictedArea(siteId: string) {
  return useRestrictedAreaWrite(
    siteId,
    ({ id, input }: { id: string; input: IRestrictedAreaInput }) => updateRestrictedArea(siteId, id, input),
    'alertSettings.restrictedArea.toasts.updated',
  );
}

export function useDeleteRestrictedArea(siteId: string) {
  return useRestrictedAreaWrite(
    siteId,
    (id: string) => deleteRestrictedArea(siteId, id),
    'alertSettings.restrictedArea.toasts.deleted',
  );
}
